import { Injectable } from '@angular/core';
import { JsonObject, OnboardingData } from '@ktypes/models';
import { BrowserStorage } from './browser-storage.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingUtilities {
  private _onboardingData: OnboardingData;
  private static readonly _attributionStorageKey = 'onboardingAttribution';
  private static readonly _attributionCapturedAtKey = 'capturedAt';
  private static readonly _attributionMaxAgeMs = 30 * 24 * 60 * 60 * 1000; // 30 days

  constructor(private _browserStorage: BrowserStorage) {
    this._onboardingData = this._browserStorage.getObject('onboarding');
  }

  get onboardingData() {
    this._onboardingData = this._browserStorage.getObject('onboarding');
    return this._onboardingData;
  }

  saveOnboardingData(onboardingData?: OnboardingData) {
    if (onboardingData != null) {
      const onboarding = {
        ...this.onboardingData,
        ...(this._browserStorage.getObject('onboarding') ?? {}),
        ...onboardingData,
      } as OnboardingData;
      this._onboardingData = onboarding;
      this._browserStorage.setObject('onboarding', onboarding);
    }
  }

  removeOnboardingData() {
    this._onboardingData = null;
    this._browserStorage.removeItem('onboarding');
  }

  cleanupOnboardingStorage() {
    this.removeOnboardingData();
    this._browserStorage.remove('eligibilityCandidate');
    this._browserStorage.remove('sharingValidation');
    this._browserStorage.remove(OnboardingUtilities._attributionStorageKey);
    this._browserStorage.remove('existingAuthData', true);
  }

  cleanupResourcefulOnboarding() {
    this._browserStorage.removeItem('completedResourcefulSurvey');
  }

  /**
   * First-touch attribution capture.
   * - Only set once per browser onboarding session (unless explicitly cleared).
   * - Stored separately from onboardingData so it can survive route changes and be reused for QR generation.
   */
  getAttribution(): JsonObject<string> | null {
    const stored =
      (this._browserStorage.getObject(OnboardingUtilities._attributionStorageKey) as JsonObject<string>) || null;
    if (!stored) {
      return null;
    }
    const capturedAt = stored[OnboardingUtilities._attributionCapturedAtKey];
    if (capturedAt) {
      const ts = Date.parse(String(capturedAt));
      if (!Number.isNaN(ts) && Date.now() - ts > OnboardingUtilities._attributionMaxAgeMs) {
        // expired; don't keep abandoned attribution around indefinitely
        this.clearAttribution();
        return null;
      }
    }
    return stored;
  }

  saveAttributionFirstTouch(attribution: JsonObject<string> | null | undefined) {
    if (!attribution || Object.keys(attribution).length === 0) {
      return;
    }
    const existing = this.getAttribution();
    if (existing && Object.keys(existing).length > 0) {
      return; // first-touch: do not overwrite
    }
    this._browserStorage.setObject(OnboardingUtilities._attributionStorageKey, {
      ...attribution,
      [OnboardingUtilities._attributionCapturedAtKey]: new Date().toISOString(),
    });
  }

  clearAttribution() {
    this._browserStorage.remove(OnboardingUtilities._attributionStorageKey);
  }

  appendAttributionToUrl(url: string): string {
    const attribution = this.getAttribution();
    if (!url || !attribution) {
      return url;
    }
    const entries = Object.entries(attribution).filter(
      ([k, v]) => k !== OnboardingUtilities._attributionCapturedAtKey && v
    );
    if (entries.length === 0) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    const qp = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    return `${url}${separator}${qp}`;
  }
}
